import { useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import type { FieldMatchingSettings } from '@/api/domains/field-matching.types';
import type { UploadedFieldMatchingSettings } from '@/components/FieldMatching';

// ----------------------------------------------------------------------

export type UseRequisitionListSettingsGridConfig = {
  renderGridHeader: (columnNumber: number) => React.ReactNode;
  fieldMatchingSettings?: FieldMatchingSettings;
};

// ----------------------------------------------------------------------

const getInitialState = (
  config: Pick<UseRequisitionListSettingsGridConfig, 'renderGridHeader'> & {
    numberOfColumnsFromSettings?: number;
  },
): UploadedFieldMatchingSettings => {
  const { numberOfColumnsFromSettings = 6, renderGridHeader } = config;

  return {
    dataSource: [],
    lockedRows: [],
    columns: Array.from({ length: numberOfColumnsFromSettings }, (_, index) => ({
      name: `${index + 1}`,
      flex: 1,
      minWidth: 220,
      header: renderGridHeader(index + 1),
    })),
  };
};

// ----------------------------------------------------------------------

export const useFieldMatchingGrid = (config: UseRequisitionListSettingsGridConfig) => {
  const { renderGridHeader, fieldMatchingSettings } = config;

  const settingsColumns = fieldMatchingSettings?.columns ?? [];
  const numberOfColumnsFromSettings = settingsColumns.reduce((numberOfCells, { number }) => {
    if (number > numberOfCells) {
      return number;
    }

    return numberOfCells;
  }, 0);

  const [{ columns, lockedRows, dataSource }, setGridSource] = useState<UploadedFieldMatchingSettings>(() =>
    getInitialState({
      numberOfColumnsFromSettings,
      renderGridHeader,
    }),
  );

  const isInitialGridSourceRef = useRef(true);

  useUpdateEffect(() => {
    if (!isInitialGridSourceRef.current) {
      return;
    }

    const initialState = getInitialState({
      numberOfColumnsFromSettings,
      renderGridHeader,
    });

    setGridSource(initialState);
  }, [numberOfColumnsFromSettings]);

  const handleUpload = (gridSource: UploadedFieldMatchingSettings) => {
    isInitialGridSourceRef.current = false;

    setGridSource(gridSource);
  };

  return {
    numberOfColumnsFromSettings,
    columns,
    lockedRows,
    dataSource,
    handleUpload,
  };
};
